import React, { useEffect, useId, useRef, useState } from 'react'
import { Box, ClickAwayListener, FormControl, Paper, Popper, TextField, Typography, useTheme } from '@mui/material'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import { debounce, Icons } from '../../../utils/utilities'

/**
 * A reusable search-and-filter list. Types into a text field, debounces the input, and once the
 * text reaches `minChars` calls `searchHandler` (an API request the caller supplies) to fetch matches.
 *
 * @param {Object} props
 * @param {String} [props.id] - id for the underlying input, also used to key the debounce timer.
 * @param {'search'|'select'} [props.variant="search"] - "search" renders the results inline below the field and just tracks the typed text. "select" shows results in a dropdown, and once an item is picked, the field displays that item's label instead of the raw search text (combobox behaviour).
 * @param {String} [props.placeholder]
 * @param {Number} [props.minChars=5] - minimum characters typed before `searchHandler` is called.
 * @param {Number} [props.debounceDelay=1000]
 * @param {Function} props.searchHandler - async (searchText) => Array<item>. Required.
 * @param {Function} [props.renderItem] - (item, index) => ReactNode. Defaults to showing `getItemLabel(item)`.
 * @param {Function} [props.getItemLabel] - (item) => String, used for the "select" variant's field display and the default renderItem.
 * @param {Function} [props.getItemKey] - (item, index) => React key.
 * @param {*} [props.value] - selected item, "select" variant only (controlled).
 * @param {Function} [props.onChange] - (item) => void, called on selection, "select" variant only.
 * @param {String} [props.emptyStateImg] - optional image shown in the empty/prompt state.
 * @param {String} [props.noResultsMessage]
 * @param {Boolean} [props.autoFocus=false]
 * @param {Boolean} [props.disabled=false]
 * @param {Boolean} [props.fullWidth=true]
 * @param {Object} [props.containerStyles={}]
 * @param {Object} [props.inputStyles={}]
 * @param {String} [props.optionsMaxHeight="350px"] - max height of the results dropdown, "select" variant only.
 */
const SearchList = ( {
    id,
    variant = "search",
    placeholder = "Type to search...",
    minChars = 5,
    debounceDelay = 1000,
    searchHandler,
    renderItem,
    getItemLabel = ( item ) => item?.name || item?.label || "",
    getItemKey = ( item, index ) => item?.id ?? index,
    value = null,
    onChange = () => { },
    emptyStateImg,
    noResultsMessage = "No results found for the search term.",
    autoFocus = false,
    disabled = false,
    fullWidth = true,
    containerStyles = {},
    inputStyles = {},
    optionsMaxHeight = "350px",
} ) => {

    const generatedId = useId()
    const fieldId = id || generatedId

    const [searchText, setSearchText] = useState( "" )
    const [results, setResults] = useState( [] )
    const [loading, setLoading] = useState( false )
    const [error, setError] = useState( null )
    const [open, setOpen] = useState( false )

    const searchInputRef = useRef()

    const { palette, border, layout } = useTheme()

    const isSelect = variant === "select"

    useEffect( () => {
        if ( isSelect && !open )
            setSearchText( value ? getItemLabel( value ) : "" )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, open] )

    const fetchResults = async ( text ) => {
        try {
            if ( text.length >= minChars ) {
                setLoading( true )
                const data = await searchHandler( text )
                setResults( data || [] )
            } else {
                setResults( [] )
            }
            setError( null )
        } catch ( err ) {
            console.log( err )
            setResults( [] )
            setError( { code: err?.response?.status || 500, message: err?.response?.data?.message || "Something went wrong while searching, please try again." } )
        } finally {
            setLoading( false )
        }
    }

    const triggerSearch = debounce( `search-list-${fieldId}`, fetchResults, debounceDelay )

    const handleChange = ( event ) => {
        const text = event.target.value
        setSearchText( text )
        if ( isSelect ) setOpen( true )
        triggerSearch( text )
    }

    const handleFocus = () => {
        if ( isSelect ) {
            setOpen( true )
            if ( value ) setSearchText( "" )
        }
    }

    const handleItemClick = ( item ) => {
        if ( !isSelect ) return
        onChange( item )
        setSearchText( getItemLabel( item ) )
        setResults( [] )
        setOpen( false )
    }

    const handleClickAway = () => {
        if ( isSelect ) setOpen( false )
    }

    const remainingChars = minChars - searchText.length

    const renderStatusBlock = () => (
        <Box borderRadius={layout.borderRadius} bgcolor={palette.contentBg} padding="20px" gap={layout.cardsGap} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
            {emptyStateImg && <Box component="img" src={emptyStateImg} height="150px" />}
            {!loading && searchText.length >= minChars && <Typography textAlign="center" variant='subtitle2'>{noResultsMessage}</Typography>}
            {!loading && searchText && searchText.length < minChars && <Typography textAlign="center" variant='subtitle2'>Type <Typography component="span" variant='subtitle2' color='customThemeColor'>{remainingChars} more character{remainingChars > 1 ? "s" : ""}</Typography> to begin the search.</Typography>}
            {!loading && !searchText && <Typography textAlign="center" variant='subtitle2'>Type <Typography component="span" variant='subtitle2' color='customThemeColor'>atleast {minChars} characters</Typography> to start searching.</Typography>}
            {loading && <DataLoadingSpinner waitingMessage="Searching, please wait..." />}
        </Box>
    )

    const renderResultsList = () => (
        <Box anchorEl={searchInputRef.current} placement='bottom-start' component={Popper} open={results.length > 0} display="flex" flexDirection="column" gap={layout.cardsGap}>
            {results.map( ( item, index ) => {
                const key = getItemKey( item, index )
                const content = renderItem ? renderItem( item, index ) : <Typography fontSize="14px">{getItemLabel( item )}</Typography>

                if ( !isSelect ) return <Box key={key}>{content}</Box>

                return (
                    <Box key={key} onClick={() => handleItemClick( item )} sx={{ cursor: "pointer", borderRadius: layout.borderRadius, "&:hover": { bgcolor: palette.contentBg } }}>
                        {content}
                    </Box>
                )
            } )}
        </Box>
    )

    const searchField = (
        <TextField
            placeholder={placeholder}
            fullWidth={fullWidth}
            inputRef={searchInputRef}
            autoFocus={autoFocus}
            disabled={disabled}
            id={fieldId}
            size='small'
            value={searchText}
            onChange={handleChange}
            onFocus={handleFocus}
            type='text'
            slotProps={{
                input: {
                    startAdornment: Icons.default.SearchIcon,
                    sx: { gap: "10px", ...inputStyles }
                }
            }}
        />
    )

    if ( isSelect ) {
        return (
            <ClickAwayListener onClickAway={handleClickAway}>
                <FormControl fullWidth={fullWidth} sx={{ position: "relative", ...containerStyles }}>
                    {searchField}
                    {open && ( searchText.length > 0 || results.length > 0 ) && (
                        <Paper sx={{ position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0, zIndex: 1300, border: border[1], boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`, maxHeight: optionsMaxHeight, overflow: "auto", padding: "10px" }}>
                            {error
                                ? <TryAgainMessageBlock parent="component" err={error.message} getData={() => fetchResults( searchText )} code={error.code} loading={loading} />
                                : results.length > 0 ? renderResultsList() : renderStatusBlock()}
                        </Paper>
                    )}
                </FormControl>
            </ClickAwayListener>
        )
    }

    return (
        <Box display="flex" flexDirection="column" gap={layout.cardsGap} sx={containerStyles}>
            <FormControl fullWidth={fullWidth}>
                {searchField}
            </FormControl>

            {results.length > 0 && renderResultsList()}
            {!error && results.length === 0 && renderStatusBlock()}
            {error && <TryAgainMessageBlock err={error.message} getData={() => fetchResults( searchText )} code={error.code} loading={loading} />}
        </Box>
    )
}

export default SearchList
